/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * matchPath / generatePath — точный порт из react-router v5 (Фаза 5,
 * docs/MIGRATION_ROUTER.md): react-router-dom удалён из зависимостей,
 * но универсальные Route/Switch и все легаси v5-пути (включая
 * regex-параметры ':id(\d+)', префикс-параметры '/shop-:shopId',
 * массивы путей, exact/strict/sensitive) должны матчиться байт-в-байт
 * как раньше. Внутри — тот же path-to-regexp@1, что использовал v5
 * (прямая зависимость в package.json с Фазы 5).
 */
import pathToRegexp from 'path-to-regexp';
import type { match } from './types';

interface CompileOptions {
  end: boolean;
  strict: boolean;
  sensitive: boolean;
}

interface CompiledPath {
  regexp: RegExp;
  keys: Array<{ name: string | number }>;
}

// кэш как в v5: по опциям, затем по паттерну
const cache: Record<string, Record<string, CompiledPath>> = {};
const cacheLimit = 10000;
let cacheCount = 0;

const compilePath = (path: string, options: CompileOptions): CompiledPath => {
  const cacheKey = `${String(options.end)}${String(options.strict)}${String(
    options.sensitive
  )}`;
  const pathCache = cache[cacheKey] ?? (cache[cacheKey] = {});

  const hit = pathCache[path];
  if (hit) {
    return hit;
  }

  const keys: Array<{ name: string | number }> = [];
  const regexp = pathToRegexp(path, keys as any, options);
  const result = { regexp, keys };

  if (cacheCount < cacheLimit) {
    pathCache[path] = result;
    cacheCount++;
  }
  return result;
};

export interface MatchPathOptions {
  path?: string | readonly string[];
  exact?: boolean;
  strict?: boolean;
  sensitive?: boolean;
}

/** Сигнатура и семантика 1:1 с react-router v5 matchPath. */
export function matchPath<
  P extends { [K in keyof P]?: string } = Record<string, string>
>(
  pathname: string,
  options: string | readonly string[] | MatchPathOptions = {}
): match<P> | null {
  const opts: MatchPathOptions =
    typeof options === 'string' || Array.isArray(options)
      ? { path: options as string | readonly string[] }
      : (options as MatchPathOptions);

  const { path, exact = false, strict = false, sensitive = false } = opts;
  const paths: readonly string[] = ([] as string[]).concat(path as any);

  return paths.reduce<match<P> | null>((matched, p) => {
    if (!p && p !== '') {
      return null;
    }
    if (matched) {
      return matched;
    }

    const { regexp, keys } = compilePath(p, {
      end: exact,
      strict,
      sensitive
    });
    const m = regexp.exec(pathname);

    if (!m) {
      return null;
    }

    const [url, ...values] = m;
    const isExact = pathname === url;

    if (exact && !isExact) {
      return null;
    }

    return {
      path: p,
      url: p === '/' && url === '' ? '/' : url,
      isExact,
      params: keys.reduce<Record<string, string>>((memo, key, index) => {
        memo[key.name] = values[index];
        return memo;
      }, {}) as P
    };
  }, null);
}

/** generatePath v5: подстановка параметров в паттерн. */
export function generatePath(
  path = '/',
  params: Record<string, unknown> = {}
): string {
  return path === '/'
    ? path
    : pathToRegexp.compile(path)(params, { pretty: true } as any);
}
